import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from './Toast';
import { submitFeedback } from '../api';

// Always-on-top, non-modal feedback launcher shown on every screen (mounted
// app-wide in App.js). Lets an operator report an improvement about the screen
// they are currently looking at — paste a screenshot (Ctrl+V) without leaving
// the page. Reuses POST /api/feedback (page_path + image_data already exist),
// so no backend change is needed. The /feedback board keeps its own form, so
// we hide this widget there to avoid two identical forms.
const CATEGORY_LABEL = { request: 'ご要望', opinion: 'ご意見', bug: '不具合', other: 'その他' };
const MAX_IMAGE_BYTES = 6 * 1024 * 1024;  // keep rows sane (backend caps ~7.5MB)
const MAX_IMAGES = 6;

export default function FloatingFeedbackWidget() {
  const toast = useToast();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('request');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);  // 複数スクショ (data URL 配列)
  const [sending, setSending] = useState(false);

  // The board at /feedback already has a submission form — don't duplicate it.
  if (location.pathname === '/feedback') return null;

  const onPaste = (e) => {
    // Attach a screenshot via Ctrl+V (clipboard image -> data URL).
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    for (const it of items) {
      if (it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (!file) continue;
        if (file.size > MAX_IMAGE_BYTES) { if (toast) toast.error('画像が大きすぎます (6MB まで)'); return; }
        const reader = new FileReader();
        reader.onload = () => setAttachments((prev) => {
          if (prev.length >= MAX_IMAGES) { if (toast) toast.error(`画像は最大 ${MAX_IMAGES} 枚までです`); return prev; }
          return [...prev, reader.result];
        });
        reader.readAsDataURL(file);
        e.preventDefault();
        return;
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const text = message.trim();
    if ((!text && attachments.length === 0) || sending) return;
    setSending(true);
    try {
      await submitFeedback({
        message: text || '(スクリーンショット)',
        category,
        // Record where it was sent from (path + query) for triage context.
        page_path: (location.pathname + (location.search || '')).slice(0, 255),
        images: attachments.length ? attachments : null,
      });
      setMessage('');
      setAttachments([]);
      if (toast) toast.success('改善要望を送信しました。ありがとうございます！');
      setOpen(false);
    } catch (err) {
      if (toast) toast.error(err.message || '送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  // Launcher (collapsed): a small floating button in the bottom-right corner.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="この画面の改善要望を送る"
        className="fixed z-[9998] bottom-5 right-5 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-full shadow-lg px-4 py-2.5 transition-colors"
      >
        改善要望
      </button>
    );
  }

  // Panel (expanded): non-modal floating card. No backdrop, so the page behind
  // stays fully interactive while the operator looks at what to improve.
  return (
    <div className="fixed z-[9998] bottom-5 right-5 w-80 max-w-[calc(100vw-2.5rem)] bg-white rounded-xl shadow-2xl border border-slate-200">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
        <h2 className="text-sm font-bold text-slate-700 m-0">この画面の改善要望</h2>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="閉じる"
          className="bg-transparent border-0 p-0 w-6 h-6 rounded hover:bg-slate-100 text-slate-500 text-lg leading-none"
        >×</button>
      </div>
      <form onSubmit={handleSubmit} className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="text-sm border border-slate-300 rounded px-2 py-1"
          >
            {Object.entries(CATEGORY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span className="text-[11px] text-slate-400 truncate" title={location.pathname}>
            画面: {location.pathname}
          </span>
        </div>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onPaste={onPaste}
          rows={3}
          placeholder="改善したい点を入力（スクショは Ctrl+V で添付）"
          className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 resize-y focus:outline-none focus:ring-2 focus:ring-sky-400"
        />
        {attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {attachments.map((src, i) => (
              <div key={i} className="relative inline-block">
                <img src={src} alt={`添付プレビュー ${i + 1}`} className="max-h-20 rounded border border-slate-300" />
                <button
                  type="button"
                  onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                  aria-label="添付を削除"
                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-slate-700 text-white text-xs leading-none flex items-center justify-center hover:bg-slate-900"
                >×</button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[11px] text-slate-400">スクショは Ctrl+V（複数可）</span>
          <button
            type="submit"
            disabled={sending || (!message.trim() && attachments.length === 0)}
            className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded px-4 py-1.5 transition-colors"
          >
            {sending ? '送信中…' : '送信'}
          </button>
        </div>
      </form>
    </div>
  );
}
